import { Task } from '@lit/task';
import { ReactiveController, ReactiveControllerHost } from 'lit';
import { ProductAPIMap } from '../constants';
import { APIMenuErrorSet, APIMenuSet } from '../context/menu-data';
import { getProductMenu } from '../services/menu-data-store';
import { ProductSlug } from '../types';

/**
 * Responsible for fetching and providing menu data
 */
export class APIMenuDataController implements ReactiveController {
  private host: ReactiveControllerHost;

  private _availableProducts: string[] = [];

  public menuData: APIMenuSet = {};

  /** Products whose fetch rejected, so the menu can say so instead of loading forever. */
  public menuErrors: APIMenuErrorSet = {};

  constructor(host: ReactiveControllerHost) {
    (this.host = host).addController(this);
  }

  public hostConnected() {
    this.initProductData();
  }

  set availableProducts(products: string[]) {
    this._availableProducts = products;
  }

  private initProductData() {
    new Task(this.host, {
      task: async ([availableProducts]) => {
        if (availableProducts == undefined) {
          this.host.requestUpdate();
          return;
        }
        const promises = availableProducts
          .filter((prod) => Object.keys(ProductAPIMap).includes(prod))
          .filter((prod) => this.menuData[prod as ProductSlug] == undefined)
          .map(async (prod) => {
            try {
              const menuData = await getProductMenu(prod as ProductSlug);
              this.menuData = {
                ...this.menuData,
                [prod as ProductSlug]: menuData,
              };
            } catch {
              // Swallowed on purpose: one product's menu failing must not stop the
              // others from resolving. The error is recorded so that product's menu
              // renders an error rather than sitting on "Loading...".
              this.menuErrors = {
                ...this.menuErrors,
                [prod as ProductSlug]: true,
              };
            }
            this.host.requestUpdate();
          });
        await Promise.all(promises);
        return;
      },
      args: () => [this._availableProducts] as const,
    });
  }
}
